import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';

interface DoughnutSegment {
  label: string;
  value: number;
  color: string;
}

interface AllocationRow {
  name: string;
  navStart: string;
  assetStart: string;
  externalCashflow: string;
  performanceYtd: string;
  navEnd: string;
  assetEnd: string;
  returnYtd: string;
  expanded?: boolean;
  level?: number;
  positive?: boolean;
}

@Component({
  selector: 'app-allocation-dashboard',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './allocation-dashboard.component.html',
  styleUrl: './allocation-dashboard.component.scss'
})

export class AllocationDashboardComponent {
  readonly navItems = [
    { icon: '▦', label: 'Overview' },
    { icon: '⌕', label: 'Search' },
    { icon: '⌂', label: 'Home' },
    { icon: '◉', label: 'Accounts' },
    { icon: '◒', label: 'Sustainability' },
    { icon: '⌁', label: 'Analytics', active: true },
    { icon: '🛒', label: 'Orders' },
    { icon: '□', label: 'Clipboard' },
    { icon: '⚖', label: 'Compliance' },
    { icon: '▭', label: 'Files' },
    { icon: '☁', label: 'Network' },
    { icon: '⚙', label: 'Settings' }
  ];

  readonly tabs = ['Allocation', 'Transaction Analytics', 'Balance', 'Cashflow', 'Profit and Loss'];

  readonly sectorSegments: DoughnutSegment[] = [
    { label: 'Financials', value: 55.65, color: '#292963' },
    { label: 'Technology', value: 22.27, color: '#e8a6ad' },
    { label: 'Consumer cyclicals', value: 10.77, color: '#fb83a9' },
    { label: 'Healthcare', value: 5.17, color: '#f4a1d1' },
    { label: 'Government activity', value: 2.13, color: '#ffdc58' },
    { label: 'Other', value: 4.02, color: '#2f3439' }
  ];

  readonly currencySegments: DoughnutSegment[] = [
    { label: 'EUR', value: 62.5, color: '#292963' },
    { label: 'USD', value: 33.57, color: '#e8a6ad' },
    { label: 'Other', value: 3.93, color: '#fb83a9' }
  ];

  readonly rows: AllocationRow[] = [
    {
      name: 'Total | Brian Hodgson ',
      navStart: '1,473,760',
      assetStart: '100.00%',
      externalCashflow: '260,027',
      performanceYtd: '166,800',
      navEnd: '1,900,586',
      assetEnd: '100.00%',
      returnYtd: '10.07%',
      expanded: true,
      positive: true
    },
    {
      name: 'Abbott Laboratories',
      navStart: '46,533',
      assetStart: '3.16%',
      externalCashflow: '-28,753',
      performanceYtd: '13,582',
      navEnd: '31,362',
      assetEnd: '1.65%',
      returnYtd: '32.89%',
      level: 1,
      positive: true
    },
    {
      name: 'Allianz SE',
      navStart: '54,342',
      assetStart: '3.69%',
      externalCashflow: '-1,978',
      performanceYtd: '17,764',
      navEnd: '70,128',
      assetEnd: '3.69%',
      returnYtd: '33.10%',
      level: 1,
      positive: true
    },
    {
      name: 'Alphabet Inc.',
      navStart: '22,731',
      assetStart: '1.54%',
      externalCashflow: '-16,639',
      performanceYtd: '98,884',
      navEnd: '104,976',
      assetEnd: '5.52%',
      returnYtd: '456.53%',
      level: 1,
      positive: true
    },
    {
      name: 'Amazon Inc.',
      navStart: '44,086',
      assetStart: '2.99%',
      externalCashflow: '29,824',
      performanceYtd: '1,605',
      navEnd: '75,514',
      assetEnd: '3.97%',
      returnYtd: '-14.20%',
      level: 1,
      positive: false
    },
    {
      name: 'AXA S.A.',
      navStart: '56,095',
      assetStart: '3.81%',
      externalCashflow: '-2,414',
      performanceYtd: '13,929',
      navEnd: '67,610',
      assetEnd: '3.56%',
      returnYtd: '25.11%',
      level: 1,
      positive: true
    },
    {
      name: 'Coca-Cola Co.',
      navStart: '33,750',
      assetStart: '2.29%',
      externalCashflow: '-596',
      performanceYtd: '4,646',
      navEnd: '37,800',
      assetEnd: '1.99%',
      returnYtd: '13.79%',
      level: 1,
      positive: true
    },
    {
      name: 'Deutsche Bank AG',
      navStart: '9,789',
      assetStart: '0.66%',
      externalCashflow: '-167',
      performanceYtd: '861',
      navEnd: '10,483',
      assetEnd: '0.55%',
      returnYtd: '8.91%',
      level: 1,
      positive: true
    },
    {
      name: 'Deutsche Post AG',
      navStart: '53,333',
      assetStart: '3.62%',
      externalCashflow: '-1,329',
      performanceYtd: '-11,602',
      navEnd: '40,402',
      assetEnd: '2.13%',
      returnYtd: '-22.03%',
      level: 1,
      positive: false
    }
  ];

  doughnutBackground(segments: DoughnutSegment[]): string {
    let start = 0;
    const stops = segments.map((segment) => {
      const end = start + segment.value;
      const value = `${segment.color} ${start}% ${end}%`;
      start = end;
      return value;
    });

    return `conic-gradient(${stops.join(', ')})`;
  }

  trackByName(_: number, row: AllocationRow): string {
    return row.name;
  }
}
